"use client";

import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import kebabToCamelCase from "@shared/functions/kebabToCamelCase";
import clsx from "clsx";
import { useFormikContext } from "formik";
import type React from "react";
import { Children } from "react";

import {
	type CommonTileProps,
	createTileClassNames,
} from "./common-tile-resources";

interface StackedTileProps extends CommonTileProps<HTMLButtonElement> {
	icon: React.ReactNode;
}

interface TileWrapperProps extends React.PropsWithChildren {}
export function StackedTileWrapper({ children }: TileWrapperProps) {
	if (Children.count(children) < 2 || Children.count(children) > 3) {
		throw Error(
			`Invalid number of tiles - must be two or 3; number of children = ${Children.count(children)}`,
		);
	}
	const classNames = clsx({
		"stacked-tiles-wrapper": true,
		"stacked-tiles-wrapper--two": Children.count(children) === 2,
		"stacked-tiles-wrapper--three": Children.count(children) === 3,
	});
	return <div className={classNames}>{children}</div>;
}
export function StackedTile({
	className,
	color,
	icon,
	label,
	componentStyle,
	value,
	...props
}: StackedTileProps) {
	const classNames = createTileClassNames(
		{ className, color, componentStyle },
		"stacked",
	);

	// biome-ignore lint/suspicious/noExplicitAny: dynamic use of fieldName from formik context
	const formikContext = useFormikContext<any>();

	// create formik field name from stepName if it exists in formik context.values
	// otherwise, use the provided fieldName (if provided)
	// this is necessary because formik field names are based on stepName, not the field name itself
	const fieldName = formikContext
		? kebabToCamelCase(formikContext?.values.stepName)
		: "";

	let loading = false;

	if (
		formikContext?.isSubmitting &&
		formikContext?.values?.[fieldName] === value
	) {
		loading = true;
	}

	if (loading) {
		icon = <FontAwesomeIcon icon={faSpinner} spin />;
	}

	return (
		<button
			disabled={loading}
			type="submit"
			className={classNames}
			value={value}
			{...props}
		>
			{icon && <span className="tile__icon">{icon}</span>}
			<span
				className="tile__label"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
				dangerouslySetInnerHTML={{ __html: label || "" }}
			/>
		</button>
	);
}
